import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../../routes/Routes';
import { MapTemplate } from '@templates';
import { LatLngTuple } from 'leaflet';
import {
  AssetTypeConfig,
  ErrorMessages,
  ServiceLocationDetailsObjectType,
} from '@types';
import {
  addressResponseType,
  errorFormatter,
  getNearestAsset,
  issueLocationValidation,
} from '@utils';
import { usePlanningBreachStore } from '../../../../hooks/usePlanningBreachStore';
import { ZodError } from 'zod';
import { SelectedLocationTextContent } from './SelectedLocationTextContent';
import {
  UNAUTHORISED_ADVERTISEMENTS,
  UNAUTHORISED_CHANGE_OF_USE,
  UNAUTHORISED_INSTALLATION,
  UNAUTHORISED_WORKS,
  BREACH_OF_PLANNING,
  SHORT_TERM_LETS,
} from '../constants/constants';

const emptyLocation: addressResponseType = {
  address_line_1: '',
  address_line_2: '',
  town: '',
  postcode: '',
  country: '',
  UPRN: '',
  USRN: '',
  latitude: '',
  longitude: '',
  easting: '',
  northing: '',
  county: '',
  full_address: '',
  asset: undefined,
};

export const IssueLocation: FunctionComponent = () => {
  const navigate = useNavigate();
  const { data, get, update } = usePlanningBreachStore();
  const issueLocationData = usePlanningBreachStore(
    (state) => state.data.issueLocationData,
  );

  const initialAddress: addressResponseType | ServiceLocationDetailsObjectType =
    get('isPhotoLocationStored') && get('serviceLocationDetailsFromFile')
      ? data.serviceLocationDetailsFromFile
      : issueLocationData?.address_line_1
        ? issueLocationData
        : ({} as addressResponseType);

  const getLatLongTuple = (
    address: addressResponseType,
  ): LatLngTuple | undefined => {
    if (address?.latitude && address?.longitude) {
      const lat = parseFloat(address?.latitude);
      const long = parseFloat(address?.longitude);
      return [lat, long];
    }
    return undefined;
  };

  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const [errorPaths, setErrorPaths] = useState<string>('');
  const [serviceLocationDetails, setServiceLocationDetails] =
    useState<addressResponseType>(initialAddress as addressResponseType);

  const [defaultLocation, setDefaultLocation] = useState<
    LatLngTuple | undefined
  >(getLatLongTuple(initialAddress as addressResponseType));
  const isPhotoLocationStored = get('isPhotoLocationStored');
  const pageTitle = isPhotoLocationStored
    ? `Check if the location is correct`
    : `Where is the issue located?`;

  const subText = isPhotoLocationStored ? (
    <div className="text-bodyLg flex flex-col gap-4">
      <p>We used your photo to find where the issue is located.</p>
      <p>You can change where the issue is located using the map.</p>
    </div>
  ) : (
    <div className="text-bodyLg flex flex-col gap-4">
      <p>Select a location on the map by searching or zooming in.</p>
      <p>If the location is a listed building, it's marked in blue.</p>
    </div>
  );

  const handleNavigate = () => {
    switch (data.issueType) {
      case UNAUTHORISED_ADVERTISEMENTS:
        navigate(RoutesList.ADVERTISEMENT_DATE);
        break;
      case UNAUTHORISED_CHANGE_OF_USE:
        navigate(RoutesList.CHANGE_DATE);
        break;
      case UNAUTHORISED_INSTALLATION:
        navigate(RoutesList.INSTALLATION_DATE);
        break;
      case UNAUTHORISED_WORKS:
        navigate(RoutesList.WORKS_DATE);
        break;
      case BREACH_OF_PLANNING:
        navigate(RoutesList.BREACH_DATE);
        break;
      case SHORT_TERM_LETS:
        navigate(RoutesList.SHORT_TERM_LETS_DATE);
      default:
        return;
    }
  };

  const handleOnPreviousClick = () => {
    navigate(RoutesList.PHOTO_UPLOAD);
  };

  const handleOnNextClick = () => {
    try {
      issueLocationValidation.parse({
        locationIssue: issueLocationData.UPRN ?? '',
      });
      if (
        !serviceLocationDetails ||
        !serviceLocationDetails.latitude ||
        !serviceLocationDetails.longitude
      ) {
        return;
      } else {
        getNearestAsset(
          Number(serviceLocationDetails.latitude),
          Number(serviceLocationDetails.longitude),
          'ListedBuildings',
        );
      }
      update('issueLocationData', serviceLocationDetails);
      setErrorMessages([]);

      handleNavigate();
    } catch (error) {
      const { errorPaths, errorMessages } = errorFormatter(error as ZodError);
      setErrorMessages(errorMessages);
      setErrorPaths(errorPaths);
    }
  };

  const handleAddressChange = (address: addressResponseType) => {
    setServiceLocationDetails(address);
    setDefaultLocation([Number(address.latitude), Number(address.longitude)]);
    const updatedServiceLocation = address.address_line_1
      ? address
      : emptyLocation;

    update(`issueLocationData`, updatedServiceLocation as unknown as any);
    setErrorMessages([]);
  };

  const buttonPrevious = {
    label: 'Previous',
    onClick: handleOnPreviousClick,
  };

  const buttonNext = {
    label: 'Next',
    onClick: handleOnNextClick,
  };

  useEffect(() => {
    if (issueLocationData) {
      const loc: LatLngTuple | undefined = getLatLongTuple(issueLocationData);
      setDefaultLocation(loc);
    }
  }, [issueLocationData]);

  const ListedBuildingAsset: AssetTypeConfig = {
    asset: 'ListedBuildings',
    color: '#a1a19a',
    fillColor: '#fbfbcc',
    fillOpacity: 0.7,
    opacity: 1,
    weight: 1.5,
  };

  return (
    <MapTemplate
      id={errorPaths}
      labelText={pageTitle}
      assetType={[ListedBuildingAsset]}
      displayMapAssets={true}
      subText={subText}
      errorPath={errorPaths}
      errorMessages={errorMessages}
      onChangeAddress={handleAddressChange}
      defaultLocation={defaultLocation}
      selectedAddress={serviceLocationDetails}
      customAddressContent={<SelectedLocationTextContent />}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
    />
  );
};
